import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MessageSquareReply } from "lucide-react";
import api from "../lib/api";
import { ukDateTime } from "../lib/dates";
import { InlineTextReply } from "./InlineTextReply";

const Bubble = ({ r, testid }) => {
  const out = r.direction === "out";
  return (
    <div data-testid={`${testid}-item-${r.id}`} className={`rounded-xl border p-3 text-sm ${out ? "ml-6" : ""}`} style={{ borderColor: "var(--sj-border)", background: out ? "#E3F1EC" : "#fff" }}>
      <div className="flex flex-wrap justify-between gap-2 text-xs" style={{ color: "var(--sj-text-3)" }}>
        <span className="font-semibold" style={{ color: "var(--sj-text)" }}>{out ? `${r.author || "Host"} → ${r.name || "member"}` : r.name || "Member"}</span>
        <span>{ukDateTime(r.created_at)}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words">{r.body}</p>
    </div>
  );
};

export const GroupReplies = ({ endpoint, showGroup = false, testid = "group-replies", title = "Replies to group texts", className = "mt-4", canReply = false }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(endpoint).then(({ data }) => setItems(data)).catch(() => setItems([])), [endpoint]);
  useEffect(() => { load(); }, [load]);
  const threads = useMemo(() => {
    const map = new Map();
    (items || []).forEach((r) => { const k = `${r.group_id}:${r.record_id || r.name}`; if (!map.has(k)) map.set(k, []); map.get(k).push(r); });
    return [...map.entries()].map(([k, list]) => ({ key: k, list: [...list].reverse() }));
  }, [items]);
  if (!items) return null;
  return (
    <div data-testid={testid} className={className}>
      <h4 className="text-sm font-semibold flex items-center gap-1.5" style={{ color: "var(--sj-primary)" }}><MessageSquareReply className="h-4 w-4" /> {title}</h4>
      {!items.length ? <p className="text-xs mt-1" style={{ color: "var(--sj-text-3)" }} data-testid={`${testid}-empty`}>No replies yet.</p> : (
        <ul className="mt-2 space-y-3 max-h-[28rem] overflow-y-auto pr-1">
          {threads.map(({ key, list }) => {
            const lastIn = [...list].reverse().find((r) => r.direction !== "out");
            return (
              <li key={key} data-testid={`${testid}-thread-${key}`} className="rounded-2xl p-2 space-y-1.5" style={{ background: "#F7F6F2" }}>
                {showGroup && list[0].group_name && <div className="text-[11px] font-semibold px-1" style={{ color: "var(--sj-text-3)" }}>{list[0].group_name}</div>}
                {list.map((r) => <Bubble key={r.id} r={r} testid={testid} />)}
                {canReply && lastIn && <InlineTextReply endpoint={`/portal/cell/group-replies/${lastIn.id}/reply`} testid={`host-reply-${lastIn.id}`} onSent={load} placeholder={`Reply to ${lastIn.name || "this member"}…`} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
